import React, { FC, useEffect } from "react";

import {
  dataViewElement,
  dataViewElementFieldsToDisplaySelector,
  DataViewKey,
  FormatType,
  KpkMedia,
  serializeComplexeParamsForRecoil,
} from "@keepeek/commons";
import {
  DisplayMode,
  ElementsMetaField,
  Format,
  FormatTechnicalFields,
} from "@keepeek/refront-components";
import { Box, ButtonBase, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import logger from "../../lib/logger-utils";
import { activeBusinessFiltersFromURLSelector } from "../../providers/businessFilter/selectors/activeFilters";

const PREFIX = "TypeByElement";

const classes = {
  root: `${PREFIX}-root`,
  element: `${PREFIX}-element`,
  arrow: `${PREFIX}-arrow`,
  format: `${PREFIX}-format`,
  media: `${PREFIX}-media`,
  elementContent: `${PREFIX}-elementContent`,
  formatContent: `${PREFIX}-formatContent`,
  formatDesc: `${PREFIX}-formatDesc`,
  applyToAll: `${PREFIX}-applyToAll`,
  formatSelector: `${PREFIX}-formatSelector`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: "grid",
    gridTemplateAreas: `"element arrow format"`,
    gridTemplateColumns: "1fr 5% 1fr",
    background: theme.palette.grey[200],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.element}`]: {
    flexGrow: 0,
    gridArea: "element",
    background: theme.palette.background.paper,
    borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateColumns: `1fr ${theme.spacing(20)}`,
    paddingRight: theme.spacing(2),
  },

  [`& .${classes.arrow}`]: {
    gridArea: "arrow",
    "& svg": {
      height: "100%",
      width: "100%",
      "& polygon": {
        fill: theme.palette.background.paper,
      },
    },
  },

  [`& .${classes.format}`]: {
    gridArea: "format",
    background: theme.palette.grey[200],
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
    display: "grid",
    gridTemplateColumns: "1fr min-content",
    gridAutoFlow: "column",
    paddingLeft: theme.spacing(2),
    justifyContent: "flex-end",
  },

  [`& .${classes.media}`]: {
    gridArea: "1/1",
    flexShrink: 0,
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
    },
  },

  [`& .${classes.elementContent}`]: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  [`& .${classes.formatContent}`]: {
    padding: theme.spacing(1),
    textAlign: "right",
    maxWidth: theme.spacing(20),
    justifySelf: "flex-end",
  },

  [`& .${classes.formatDesc}`]: {
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.applyToAll}`]: {
    color: theme.palette.primary.main,
    textAlign: "right",
    textDecoration: "underline",
  },

  [`& .${classes.formatSelector}`]: {
    padding: theme.spacing(1, 1, 1, 0),
  },
}));

export type DownloadManagerFormatsByElement = {
  element: KpkMedia["id"];
  formats: FormatType[];
  type: string;
};

export type TypeByElementProps = {
  formatsByElement: DownloadManagerFormatsByElement;
  onSelectFormat: (format: FormatType["id"]) => void;
  onApplyToAll: (format: FormatType["id"]) => void;
  dataviewkey: DataViewKey;
};

const TypeByElement: FC<React.PropsWithChildren<TypeByElementProps>> = function ({
  formatsByElement,
  onSelectFormat,
  onApplyToAll,
  dataviewkey,
}) {
  const { t } = useTranslation();
  const filters = useRecoilValue(activeBusinessFiltersFromURLSelector);
  // Todo: should look to every possible context
  const element: KpkMedia | undefined = useRecoilValue(
    dataViewElement(
      serializeComplexeParamsForRecoil({
        id: formatsByElement.element,
        key: dataviewkey,
        filters,
      }),
    ),
  );
  const { functionalFieldsToDisplay = [], technicalFieldsToDisplay = [] } = useRecoilValue(
    dataViewElementFieldsToDisplaySelector({
      id: formatsByElement.element,
      key: dataviewkey,
    }),
  );

  // Select the first format from an element if this is not yet the case
  useEffect(() => {
    if (!formatsByElement.formats.some((f) => f.active)) {
      onSelectFormat(formatsByElement.formats[0].id);
    }
  }, [formatsByElement.formats, onSelectFormat]);

  // The corresponding element should be in the store
  if (!element) {
    return null;
  }

  const title: string = (!Array.isArray(element?.title?.value) && element?.title?.value) || "";
  const activeFormat: FormatType | undefined = formatsByElement.formats.find((f) => f.active);

  return (
    <StyledBox className={clsx("TypeByElement", classes.root)} component="li">
      <Box className={classes.element}>
        <Box className={classes.media}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={element.previewUrl} alt={title} />
        </Box>
        <Box className={classes.elementContent}>
          <Typography variant="subtitle2">
            {t("download-manager.type-by-element.original.lalel")}
          </Typography>
          <Box>
            <ElementsMetaField
              fields={functionalFieldsToDisplay}
              media={element}
              dataViewKey={dataviewkey}
            />
            <FormatTechnicalFields
              element={element}
              technicalFieldsToDisplay={technicalFieldsToDisplay}
              folderDisplayMode={DisplayMode.FIRST_FOLDER_WITHOUT_LINK}
              KIcon={KIcon}
            />
          </Box>
        </Box>
      </Box>
      <Box className={classes.arrow} aria-hidden>
        <svg preserveAspectRatio="none" viewBox="0 0 2 2">
          <polygon points="0,0 2,1 0,2" />
        </svg>
      </Box>
      <Box className={classes.format}>
        <Box className={classes.formatContent}>
          <Typography variant="subtitle2">
            {t("download-manager.type-by-element.selected.lalel")}
          </Typography>
          {activeFormat && (
            <Typography className={classes.formatDesc} variant="body2">
              {activeFormat.description}
            </Typography>
          )}
          {activeFormat && (
            <ButtonBase
              focusRipple
              className={classes.applyToAll}
              onClick={() => onApplyToAll(activeFormat.id)}
            >
              {t("download-manager.type-by-element.apply-to-all.lalel")}
            </ButtonBase>
          )}
        </Box>
        <Box className={classes.formatSelector}>
          <Format format={activeFormat} onClick={() => logger.info("changeFormat")} KIcon={KIcon} />
        </Box>
      </Box>
    </StyledBox>
  );
};

export default TypeByElement;
